import {
  faDiscord,
  faLinkedin,
  faTelegram,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import {
  faBadgeCheck,
  faFile,
  faGlobe,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { json } from "@remix-run/cloudflare";
import { useLoaderData } from "@remix-run/react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { BlockchainIcon } from "~/components/logos/blockchain";
import { CoinGecko } from "~/components/logos/coingecko";
import { CoinMarketCap } from "~/components/logos/coinmarketcap";
import { Farcaster } from "~/components/logos/farcaster";
import { CTAPrimary } from "~/components/site/call-to-action-primary";
import { Copy } from "~/components/site/copy";
import { Error } from "~/components/site/error";
import { ExternalLink } from "~/components/site/external-link";
import { container, item } from "~/components/site/layout";
import { Posts } from "~/components/site/posts";
import { Callout } from "~/components/stablecoins/callout";
import { FormattedCurrency } from "~/components/stablecoins/formatted-currency";
import { Layout } from "~/components/stablecoins/layout";
import { Token as TokenElement } from "~/components/stablecoins/token";
import { getTitle, RESPONSES, ROUTE_DATA } from "~/lib/constants";
import type { SellerId, WalletId } from "~/lib/ecosystem";
import { Company, SELLERS, WALLETS } from "~/lib/ecosystem";
import type { Ctx } from "~/lib/env";
import { getMeta } from "~/lib/get-meta";
import type { Urls } from "~/lib/get-urls";
import { getUrls, isLocal } from "~/lib/get-urls";
import { getSelectedPosts } from "~/lib/post";
import { required } from "~/lib/required";
import type {
  Link,
  LinkType,
  SocialType,
  StablecoinData,
  Token,
} from "~/lib/stablecoins.server";
import { getStablecoin } from "~/lib/stablecoins.server";
import {
  AttestationsGraphs,
  hasAttestationGraph,
} from "~/routes/stablecoins/attestation-graphs";

export const publicTickers = new Set(["SBC", "USDGLO", "USC", "MXNe", "USD*"]);
export const attestedTokens = new Set(["SBC", "USDGLO"]);

const links = [
  "Website",
  "Whitepaper",
  "Attestations",
  "CoinGecko",
  "CoinMarketCap",
];

const { path } = ROUTE_DATA.stablecoins;

type Props = {
  selectedItems: string[];
  items: Company[];
  selectedWallets?: WalletId[] | string[];
  selectedSellers?: SellerId[] | string[];
};

type IconProps = {
  type: LinkType | SocialType;
};

type LoaderData = Readonly<{
  isLocal: boolean;
  token: Token;
  urls: Urls;
}>;

export async function loader({ context, params, request }: LoaderFunctionArgs) {
  const token: Token = await getStablecoin(
    required(params.ticker, "params.ticker"),
    context as Ctx
  );

  if (token == null || !token.enabled) throw RESPONSES.notFound();

  return json({
    isLocal: isLocal(request),
    token,
    urls: {
      ...getUrls({ path: `${path}/${token.ticker}`, request }),
    },
  });
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data?.token == null) return [];

  const { token, urls } = data;
  const imageUrl = `${urls.base}${token.ogImage}`;

  return [
    ...getMeta({
      desc: `View ${token.ticker} data, including live charts of supply, chain information, and attestations at Brale.`,
      title: getTitle(`${token.ticker} (${token.name})`),
      urls,
    }),
    { property: "og:image", content: imageUrl },
    { property: "og:type", content: "article" },
    { name: "twitter:alt", content: token.ticker },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: imageUrl },
  ];
};

export default function TokenTicker(): JSX.Element {
  const { isLocal, token } = useLoaderData<LoaderData>();

  const [data, setData] = useState<StablecoinData>();

  useEffect(() => {
    async function getData() {
      if (token == null) return;

      setData(
        isLocal
          ? DUMMY_STABLECOIN_DATA
          : await (
              await fetch(
                `https://app.brale.xyz/api/stablecoins/${token.ticker}`
              )
            ).json()
      );
    }

    if (data == null) getData();
  }, [data, isLocal, token]);

  return token != null ? (
    <Layout>
      <motion.section
        variants={item}
        className={`border-4 rounded-3xl overflow-clip mb-24 lg:mb-36 ${token.borderColor}`}
      >
        <div className="border border-primary rounded-[20px] overflow-clip">
          <div className="bg-secondary p-6 md:p-8 border-b border-primary">
            <TokenElement
              buyURL={buyUrl(token.ticker)}
              ticker={token.ticker}
              name={token.name}
              imageURL={token.icon}
              deployments={token.deployments}
            />
          </div>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2"
          >
            <Callout
              heading={`About ${token.ticker}`}
              external={false}
              className="md:col-span-2"
            >
              <p className="text-base md:text-xl text-primary">
                {token.overview}
              </p>
            </Callout>
            <Callout heading="Market cap" external={false}>
              <div className="text-2xl font-semibold text-primary">
                {data?.total_supply && data.total_supply < 1000000 ? (
                  `< $1,000,000 ${token.reserveCurrency}`
                ) : (
                  <FormattedCurrency
                    value={data?.total_supply || 0}
                    currencyCode={token.reserveCurrency}
                  />
                )}
              </div>
            </Callout>
            <Callout heading="Contracts">
              <ul
                className={`flex flex-wrap gap-3 ${
                  token.deployments.length > 10 ? "lg:gap-0" : ""
                } xl:gap-3`}
              >
                {token.deployments.map((d, i) => (
                  <li
                    key={d.chain.id}
                    className={`group relative ${
                      token.deployments.length > 10 ? "lg:-mr-1" : ""
                    }`}
                  >
                    <ExternalLink
                      className="flex items-center"
                      title="View in explorer"
                      href={d.explorerAddress}
                    >
                      <BlockchainIcon
                        chainId={d.chain.id}
                        className="!h-8 !w-8"
                      />
                    </ExternalLink>
                    <div
                      className={`absolute pt-2 invisible group-hover:visible z-50 ${
                        i >= 5 ? "right-0" : ""
                      }`}
                    >
                      <div className="hidden md:flex items-center gap-x-4 bg-secondary px-3 py-2 text-sm text-primary rounded-md mx-auto border border-primary whitespace-nowrap">
                        <div className="flex items-center gap-x-2">
                          <BlockchainIcon
                            chainId={d.chain.id}
                            className="!h-4 !w-4"
                          />
                          {shortAddress(d.address)}
                        </div>
                        <Copy value={d.address} className="!h-4 !w-4" />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Callout>
            <Callout heading="Where to buy">
              <Sellers
                selectedSellers={token.sellers}
                selectedItems={[]}
                items={[]}
                ticker={token.ticker}
              />
            </Callout>
            <Callout heading="Recommended wallets">
              <Wallets
                selectedWallets={token.wallets}
                selectedItems={[]}
                items={[]}
                ticker={token.ticker}
              />
            </Callout>
            <Callout heading="Links" className="md:!border-none">
              <ul className="flex flex-wrap gap-3">
                {token.links.map((d) => (
                  <LinkItem key={d.url} {...d} />
                ))}
              </ul>
            </Callout>
            {token.social && (
              <Callout heading="Social" className="!border-none">
                <ul className="flex gap-x-3">
                  {token.social.map((d) => (
                    <LinkItem key={d.url} {...d} />
                  ))}
                </ul>
              </Callout>
            )}
          </motion.div>
        </div>
      </motion.section>
      {token.benefits && (
        <motion.section
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="section-sub prose dark:prose-invert"
        >
          <motion.h2 variants={item} className="text-5xl mt-0 mb-6">
            Benefits of {token.ticker}
          </motion.h2>
          <div className="flex flex-col sm:grid sm:grid-cols-3 blurb-gap mt-12">
            {token.benefits.map((d) => (
              <motion.div variants={item} key={d.title}>
                <FontAwesomeIcon
                  className="h-12 w-12 mb-3 text-purple-500"
                  icon={d.icon}
                />
                <h4 className="mt-0 mb-4 text-xl">{d.title}</h4>
                <div className="blurb">{d.text}</div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}
      {hasAttestationGraph(token.ticker) && (
        <motion.section
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="section-sub prose dark:prose-invert"
        >
          <div className="lg:grid lg:grid-cols-2 items-start gap-16">
            <motion.div variants={item} className="lg:sticky top-8">
              <h2 className="text-5xl mt-0 mb-6">Transparency</h2>
              <p className="subheading mt-2 mb-12">{transparencyText(token)}</p>
            </motion.div>
            <div className="flex flex-col blurb-gap mt-3">
              <motion.div variants={item}>
                {hasAttestationGraph(token.ticker) && AttestationsGraphs(token)}
              </motion.div>
              {attestedTokens.has(token.ticker) &&
                hasAttestationGraph(token.ticker) && (
                  <motion.div variants={item} className="mt-0">
                    <h3 className="h4 mt-0 mb-3">Monthly Reserve Reports</h3>
                    <p>
                      Monthly reports are issued by an independent accounting
                      firm to provide assurance that the value of stablecoin
                      reserves are equal to the amount in circulation. Reports
                      are prepared according to the attestation standards of the
                      American Institute of Certified Public Accountants
                      (AICPA).
                    </p>
                    <ol className="not-prose divide-y divide-gray-300 dark:divide-gray-700">
                      <li className="flex items-start gap-x-4 py-4 pl-3">
                        <span className="text-gray-500 dark:text-gray-400">
                          2023
                        </span>
                        <ol className="flex flex-wrap items-center gap-x-2 gap-y-4">
                          {[
                            { month: "Oct", num: "10" },
                            { month: "Nov", num: "11" },
                            { month: "Dec", num: "12" },
                          ].map(({ month, num }) => (
                            <li key={month}>
                              <ExternalLink
                                className="attestation"
                                href={`/assets/reports/${
                                  token.ticker
                                }-${formatTokenName(
                                  token.name
                                )}-Reserve-Attestation-Report-${num}-2023.pdf`}
                              >
                                {month}
                              </ExternalLink>
                            </li>
                          ))}
                        </ol>
                      </li>
                      <li className="flex items-start gap-x-4 py-4 pl-3">
                        <span className="text-gray-500 dark:text-gray-400">
                          2024
                        </span>
                        <ol className="flex flex-wrap items-center gap-x-2 gap-y-4">
                          {[
                            { month: "Jan", num: "01" },
                            { month: "Feb", num: "02" },
                            { month: "Mar", num: "03" },
                            { month: "Apr", num: "04" },
                            { month: "May", num: "05" },
                            { month: "Jun", num: "06" },
                            { month: "Jul", num: "07" },
                            { month: "Aug", num: "08" },
                            { month: "Sept", num: "09" },
                          ].map(({ month, num }) => (
                            <li key={month}>
                              <ExternalLink
                                className="attestation"
                                href={`/assets/reports/${
                                  token.ticker
                                }-${formatTokenName(
                                  token.name
                                )}-Reserve-Attestation-Report-${num}-2024.pdf`}
                              >
                                {month}
                              </ExternalLink>
                            </li>
                          ))}
                        </ol>
                      </li>
                    </ol>
                  </motion.div>
                )}
            </div>
          </div>
        </motion.section>
      )}
      {token.posts && (
        <motion.section
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="section-sub prose dark:prose-invert"
        >
          <motion.div variants={item}>
            <h2 className="text-5xl mt-0 mb-12">{token.ticker} News</h2>
          </motion.div>
          <Posts posts={getSelectedPosts(token.posts)} />
        </motion.section>
      )}
      {publicTickers.has(token.ticker) && (
        <motion.div
          variants={item}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <CTAPrimary
            heading={`Get started with ${token.ticker}`}
            subheading={`Sign up to buy and sell ${token.ticker}.`}
            primaryBtn={`Apply to buy`}
            primaryBtnUrl={`https://app.brale.xyz/buy/signup/${token.ticker}`}
            className="!p-0 prose dark:prose-invert"
          />
        </motion.div>
      )}
    </Layout>
  ) : (
    <></>
  );
}

const transparencyText = (token: Token) => {
  switch (token.reserveCurrency) {
    case "USD":
      return `${token.ticker} is always redeemable 1:1 for U.S. dollars. Brale asserts that the balance of treasury assets is equal to or greater than the tokens issued, as of the following dates.`;
    case "MXN":
      return `${token.ticker} is always redeemable 1:1 for the equal value of Mexican Pesos. Brale asserts that the balance of treasury assets is equal to or greater than the tokens issued, as of the following dates.`;
  }
};

export function CatchBoundary() {
  return <Error statusCode={404} themed />;
}

const buyUrl = (ticker: string) => {
  return publicTickers.has(ticker)
    ? `https://app.brale.xyz/buy/signup/${ticker}`
    : undefined;
};

const shortAddress = (address: string) =>
  address.length >= 13
    ? `${address.slice(0, 6)}...${address.slice(-6)}`
    : address;

const EcosystemItem = ({
  id,
  name,
  url,
  icon,
  ticker,
}: Company & { ticker: string }) => (
  <li key={id}>
    <ExternalLink
      className="flex items-center"
      title={`Visit ${name}`}
      href={id === "brale" ? buyUrl(ticker) : url}
    >
      <img
        src={icon}
        alt={`${name} logo`}
        className="h-8 w-8 rounded-full border border-primary"
      />
    </ExternalLink>
  </li>
);

function RenderItems({
  selectedItems = [],
  items = [],
  ticker,
}: Props & { ticker: string }): JSX.Element {
  const itemMap = new Map<string, Company>();

  items.forEach((item) => {
    itemMap.set(item.id, item);
  });

  const filteredItems = selectedItems
    .map((id) => itemMap.get(id) as Company)
    .filter(Boolean);

  return (
    <ul className="flex gap-x-3">
      {filteredItems.map((item) => (
        <EcosystemItem key={item.id} {...item} ticker={ticker} />
      ))}
    </ul>
  );
}

function Wallets({
  selectedWallets = [],
  ticker,
}: Props & { ticker: string }): JSX.Element {
  return (
    <RenderItems
      selectedItems={selectedWallets}
      items={WALLETS}
      ticker={ticker}
    />
  );
}

function Sellers({
  selectedSellers = [],
  ticker,
}: Props & { ticker: string }): JSX.Element {
  return (
    <RenderItems
      selectedItems={selectedSellers}
      items={SELLERS}
      ticker={ticker}
    />
  );
}

const IconComponent = ({ type }: IconProps) => {
  switch (type) {
    case "Website":
      return <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faGlobe} />;
    case "Whitepaper":
      return <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faFile} />;
    case "Attestations":
      return (
        <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faBadgeCheck} />
      );
    case "Twitter":
      return <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faXTwitter} />;
    case "LinkedIn":
      return <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faLinkedin} />;
    case "Telegram":
      return <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faTelegram} />;
    case "Discord":
      return <FontAwesomeIcon className="h-4 w-4 m-[1px]" icon={faDiscord} />;
    case "Farcaster":
      return <Farcaster className="h-4 w-4 m-[1px]" />;
    case "CoinGecko":
      return <CoinGecko className="h-4 w-4 m-[1px]" />;
    case "CoinMarketCap":
      return <CoinMarketCap className="h-4 w-4 m-[1px]" />;
    default:
      return null;
  }
};

const LinkItem = ({ type, url }: Link) => {
  return (
    <li key={url}>
      <ExternalLink
        className={`flex items-center ${
          links.includes(type) ? "px-4" : "px-2"
        } py-2 gap-x-2 bg-secondary border border-primary rounded-full text-sm global-hover-transition hover:border-gray-400 hover:dark:border-gray-600 text-primary`}
        title={`View ${type}`}
        href={url}
      >
        <IconComponent type={type} />
        {links.includes(type) && <span className="leading-[14px]">{type}</span>}
      </ExternalLink>
    </li>
  );
};

const formatTokenName = (name: Token["name"]) => name.replace(/\s+/g, "-");

const DUMMY_STABLECOIN_DATA: StablecoinData = {
  chain_time_chart: {
    values: {
      Algorand: [
        178611518.212096, 178764182.573056, 178764182.573056, 175288760.4224,
        176605091.311616, 176221826.562048, 175915636.482048, 175216027.252736,
        174585446.5024, 177338577.481728, 171730883.741696, 176926469.332992,
        176586017.511424, 176254597.722112, 176000832.241664, 176650396.751872,
        173154318.813184, 175316529.702912, 171895543.781376, 171880595.642368,
        171462730.211328, 172548770.312192, 177468956.542976, 180804273.852416,
        179736629.213184, 180219367.553024, 179826442.602496, 182711181.162496,
        182609456.603136, 186139183.562752,
      ],
      Avalanche: [
        1257109648.3041081, 1257073816.9040089, 1256359579.174643,
        1260761289.561641, 1188306202.6015878, 1187435524.9277775,
        1186856247.330385, 1185528937.3607497, 1146828092.7086725,
        1147134769.0539715, 1126032781.639359, 1123133072.5081716,
        1125108063.5521126, 1122792379.160749, 1122734974.325595,
        1087853320.45052, 1081292061.0892186, 1074916293.347871,
        1082935831.587881, 1074417206.4074905, 1073501494.8264468,
        1053978973.7566016, 1049176312.6643538, 1089516595.5422497,
        1093903670.694628, 1092036192.6227155, 1092344621.2080214,
        1101373522.6587856, 1140210055.8572938, 1129085440.9592261,
      ],
      Ethereum: [
        34788729723.815865, 34726961070.21954, 34672008901.97761,
        34713111361.678696, 34621512194.41676, 34616062335.76256,
        34614863936.961075, 34579342538.62853, 34601542838.21636,
        34517124187.13812, 34091069812.49671, 34038580969.142345,
        34107787892.168594, 34129616473.41853, 33989965595.429718,
        34080179344.80618, 34118506711.61962, 34130018162.40272,
        34138570846.289425, 34098386922.807236, 34088976843.781086,
        33810491166.881687, 33383055643.284504, 32879851842.758156,
        32462650549.479794, 32532298018.08249, 32455851635.840145,
        32702800628.06054, 33390475657.49054, 33385129981.760536,
      ],
      Flow: [
        15575608, 15927864, 15927864, 15927864, 15927864, 15927864, 15927864,
        15525584, 15521977, 16186238, 14636753, 15005202, 15583628, 15603408,
        15566499, 16332886, 15975935, 16535124, 16535124, 16021380, 15942736,
        17151978, 15726061, 15575183, 15947549, 16022661, 15925889, 16652974,
        16125832, 16608756,
      ],
      Hedera: [
        10452413, 10768179, 10768179, 10768179, 10768179, 10768179, 10768179,
        10787207, 11056108, 11703310, 10130593, 11280681, 11077993, 11103806,
        11087956, 11894717, 11746793, 12304597, 12304597, 12140473, 11703158,
        12921988, 11527481, 11481168, 11636090, 12176070, 12118572, 12842998,
        12330824, 12879509,
      ],
      Solana: [
        2157861023.9146743, 2176051594.539962, 2128675338.4106152,
        2111985977.8879933, 2066044847.4975488, 2066095134.3231845,
        2067209003.325951, 2069896951.6755702, 2071212189.4272342,
        2078373489.630788, 2087097737.4891684, 2098209819.2138875,
        2097197517.846194, 2095878600.1716754, 2079725208.6941872,
        2035621128.4595819, 1999621608.104586, 2005938480.5114326,
        2007264337.7804863, 2011445598.3364089, 2012838431.4339714,
        2022326987.0660486, 2023303959.3210318, 2014630269.058278,
        2012521102.2490926, 2011678580.54084, 2007206492.525369,
        2111104870.3558269, 2182721829.300299, 1880465923.3895366,
      ],
      Stellar: [
        181236756, 181681448, 181696969, 181710808, 181707731, 181707774,
        181694673, 181753575, 181803784, 182337201, 191154022, 191501811,
        192011165, 194537990, 194742194, 195415563, 194653252, 195276837,
        195251782, 194770894, 194501843, 193590855, 194034023, 193827565,
        193893497, 188875807, 188769050, 189697410, 189903186, 188987118,
      ],
      Tron: [
        1966252999.92, 1964667309.14, 1976881552.46, 1682928274.69,
        1539671773.37, 1537791403.13, 1537459650.69, 1371906605.6,
        1265872010.36, 1272191338.33, 1273174397.19, 1274146316.32,
        1272896678.01, 1267319004.43, 1269746172.04, 1266052864.75,
        1352816700.71, 1367072179.77, 1225093230.19, 1215938961.92,
        1212534552.89, 1223558033.98, 1238852901.2, 1222061200.94,
        1202705096.59, 1198239517.51, 1199009506.64, 1200166679.59,
        1215833278.02, 1222201382.03,
      ],
    },
    dates: [
      1665446400, 1665532800, 1665619200, 1665705600, 1665792000, 1665878400,
      1665964800, 1666051200, 1666137600, 1666224000, 1666310400, 1666396800,
      1666483200, 1666569600, 1666656000, 1666742400, 1666828800, 1666915200,
      1667001600, 1667088000, 1667174400, 1667260800, 1667347200, 1667433600,
      1667520000, 1667606400, 1667692800, 1667779200, 1667865600, 1668011975,
    ],
  },
  supply_chart: {
    chain_names: [
      "Ethereum",
      "Solana",
      "TRON",
      "Avalanche",
      "Stellar",
      "Algorand",
      "Flow",
      "Hedera",
    ],
    supply_amounts: [
      33385129981.760536, 1880465923.3895366, 1222201382.03, 1129085440.9592261,
      188987118, 186139183.562752, 16608756, 12879509,
    ],
  },
  total_supply: 42115041752.1867,
};
